import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaBolt, FaLightbulb, FaCalculator, FaBatteryFull, FaChartPie } from 'react-icons/fa';

const OhmsLawCalculator = () => {
  const [voltage, setVoltage] = useState('');
  const [current, setCurrent] = useState('');
  const [resistance, setResistance] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateValues = () => {
    let v, i, r;
    
    if (voltage && current) {
      v = parseFloat(voltage);
      i = parseFloat(current);
      r = v / i;
      setResistance(r.toFixed(2));
      setShowGraph(true);
    } else if (voltage && resistance) {
      v = parseFloat(voltage);
      r = parseFloat(resistance);
      i = v / r;
      setCurrent(i.toFixed(2));
      setShowGraph(true);
    } else if (current && resistance) {
      i = parseFloat(current);
      r = parseFloat(resistance);
      v = i * r;
      setVoltage(v.toFixed(2));
      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!voltage || !current || !resistance) return null;

    const v = parseFloat(voltage);
    const i = parseFloat(current);
    const r = parseFloat(resistance);

    const series = [v, i * 1000, r]; // Convert current to mA for better visualization
    const labels = ['Voltage (V)', 'Current (mA)', 'Resistance (Ω)'];

    return {
      series,
      options: {
        chart: {
          type: 'donut',
          height: 350,
        },
        labels,
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: 'bottom',
              },
            },
          },
        ],
        legend: {
          position: 'bottom',
          fontSize: '14px',
        },
        colors: ['#FF6384', '#36A2EB', '#FFCE56'],
        dataLabels: {
          enabled: true,
          formatter: function(val, opts) {
            return opts.w.config.labels[opts.seriesIndex] + ': ' + val;
          },
        },
      },
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-50 to-indigo-50">
      <Helmet>
        <title>Ohm's Law Calculator | Physics Tool</title>
        <meta name="description" content="Calculate voltage, current, and resistance using Ohm's Law. Interactive calculator with pie chart visualization." />
        <meta name="keywords" content="ohm's law calculator, physics calculator, electrical calculator, voltage calculator, current calculator, resistance calculator" />
        <meta property="og:title" content="Ohm's Law Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate voltage, current, and resistance using Ohm's Law. Interactive calculator with pie chart visualization." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ohm's Law Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate voltage, current, and resistance using Ohm's Law. Interactive calculator with pie chart visualization." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/ohms-law" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaBolt />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Ohm's Law Calculator</h1>
          <p className="text-xl text-gray-600">Calculate electrical parameters using Ohm's Law</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Voltage (V)</label>
                <input
                  type="number"
                  value={voltage}
                  onChange={(e) => setVoltage(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter voltage (V)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Current (A)</label>
                <input
                  type="number"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter current (A)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Resistance (Ω)</label>
                <input
                  type="number"
                  value={resistance}
                  onChange={(e) => setResistance(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter resistance (Ω)"
                />
              </div>
              <div>
                <button
                  onClick={calculateValues}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate
                </button>
              </div>

              {voltage && current && resistance && (
                <div className="mt-4 p-4 bg-blue-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-blue-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-blue-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-blue-800">Voltage = {voltage} V</p>
                        <p className="text-blue-800">Current = {current} A</p>
                        <p className="text-blue-800">Resistance = {resistance} Ω</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using Ohm's Law:
                      <br />
                      V = I × R
                      <br />
                      <br />
                      Where:
                      <br />
                      V = Voltage = {voltage} V
                      <br />
                      I = Current = {current} A
                      <br />
                      R = Resistance = {resistance} Ω
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Values Distribution</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="donut"
                height={350}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows the distribution of voltage, current (in mA), and resistance
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter any two values (voltage, current, or resistance)</li>
            <li>Click "Calculate" to get the third value</li>
            <li>View the pie chart showing the distribution of values</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Three-Parameter Calculation</h3>
              <p>Calculate any electrical parameter using two known values</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>View the distribution of values in a pie chart</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for physics and electrical engineering students</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Ohm's Law?</h3>
              <p className="text-gray-600">
                Ohm's Law states that the current through a conductor between two points is directly proportional to the voltage across the two points, and inversely proportional to the resistance between them.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Voltage should be in volts (V)</li>
                  <li>Current should be in amperes (A)</li>
                  <li>Resistance should be in ohms (Ω)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the pie chart work?</h3>
              <p className="text-gray-600">
                The pie chart shows:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Relative proportions of voltage, current (in mA), and resistance</li>
                  <li>Current is converted to mA for better visualization</li>
                  <li>Each slice represents one of the three parameters</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Ohm's Law is fundamental in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Circuit design</li>
                  <li>Electrical troubleshooting</li>
                  <li>Power calculations</li>
                  <li>Electronics engineering</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Engineers design electrical circuits</li>
                  <li>Students understand electrical concepts</li>
                  <li>Technicians troubleshoot electrical systems</li>
                  <li>Anyone needing to calculate electrical parameters</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OhmsLawCalculator;
